import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  ArrowLeftIcon,
  CreditCardIcon,
  UserIcon,
  ClockIcon,
  DocumentTextIcon,
  ArrowDownTrayIcon,
  CheckCircleIcon,
  BuildingOfficeIcon,
  BanknotesIcon,
  CalendarIcon,
  DocumentDuplicateIcon,
  XCircleIcon,
  ArrowUturnLeftIcon,
} from '@heroicons/react/24/outline';
import { paymentsAPI, auditLogsAPI } from '../services/api';
import { Card, Button, LoadingSpinner, StatusBadge, PageHeader, DataTableCard, Table } from '../components/ui';
import { Payment, TableColumn } from '../types';
import { usePermissions } from '../hooks/usePermissions';
import toast from 'react-hot-toast';

const METHOD_LABELS: Record<string, string> = {
  EFECTIVO: 'Efectivo',
  TRANSFERENCIA_BANCARIA: 'Transferencia Bancaria',
  TRANSFERENCIA_MOVIL: 'Transferencia Móvil',
  CHEQUE: 'Cheque',
  DEPOSITO_BANCARIO: 'Depósito Bancario',
};

const METHOD_COLORS: Record<string, string> = {
  EFECTIVO: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
  TRANSFERENCIA_BANCARIA: 'bg-blue-50 text-blue-700 ring-1 ring-blue-200',
  TRANSFERENCIA_MOVIL: 'bg-violet-50 text-violet-700 ring-1 ring-violet-200',
  CHEQUE: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
  DEPOSITO_BANCARIO: 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200',
};

const STATUS_TONE: Record<string, 'success' | 'danger' | 'warning' | 'neutral' | 'info'> = {
  CONFIRMED: 'success',
  CANCELLED: 'danger',
  REFUNDED: 'neutral',
  PARTIAL_REFUND: 'warning',
  PENDING: 'info',
};

const STATUS_LABEL: Record<string, string> = {
  CONFIRMED: 'Confirmado',
  CANCELLED: 'Anulado',
  REFUNDED: 'Reembolsado',
  PARTIAL_REFUND: 'Reembolso parcial',
  PENDING: 'Pendiente',
};

const formatCurrency = (minor?: number, major?: number) => {
  const amount = minor != null ? minor / 100 : Number(major || 0);
  return `$${amount.toLocaleString('es-DO', { minimumFractionDigits: 2 })}`;
};

const formatDate = (dateStr?: string) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('es-DO', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const TRANSFER_METHODS = ['TRANSFERENCIA_BANCARIA', 'TRANSFERENCIA_MOVIL', 'DEPOSITO_BANCARIO'];

/* ─── Cancel Modal ─── */
const CancelModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  isLoading: boolean;
}> = ({ isOpen, onClose, onConfirm, isLoading }) => {
  const [reason, setReason] = useState('');
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-900">Anular pago</h3>
        <p className="text-sm text-slate-600">Las facturas asociadas serán revertidas a su saldo anterior.</p>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1.5">Motivo de anulación</label>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            rows={3}
            placeholder="Describe el motivo…"
            className="w-full px-3 py-2.5 text-sm rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-400 resize-none"
          />
        </div>
        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded-xl border border-slate-200 text-slate-600 bg-white hover:bg-slate-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => onConfirm(reason)}
            disabled={isLoading}
            className="px-5 py-2 text-sm font-semibold rounded-xl bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
          >
            {isLoading ? 'Anulando…' : 'Confirmar anulación'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Refund Modal ─── */
const RefundModal: React.FC<{
  isOpen: boolean;
  maxAmount: number;
  onClose: () => void;
  onConfirm: (amount: number, reason: string) => void;
  isLoading: boolean;
}> = ({ isOpen, maxAmount, onClose, onConfirm, isLoading }) => {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');

  if (!isOpen) return null;

  const parsedAmount = parseFloat(amount) || 0;
  const isValid = parsedAmount > 0 && parsedAmount <= maxAmount && reason.trim().length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-900">Procesar reembolso</h3>
        <p className="text-sm text-slate-600">
          Monto máximo disponible: <span className="font-semibold text-slate-800">{formatCurrency(Math.round(maxAmount * 100))}</span>
        </p>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1.5">Monto a reembolsar *</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
            <input
              type="number"
              step="0.01"
              min="0.01"
              max={maxAmount}
              value={amount}
              onChange={e => setAmount(e.target.value)}
              placeholder="0.00"
              className="w-full pl-7 pr-3 py-2.5 text-sm rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-amber-400"
            />
          </div>
          {parsedAmount > maxAmount && (
            <p className="text-xs text-rose-600 mt-1">El monto supera el disponible</p>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1.5">Motivo del reembolso *</label>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            rows={3}
            placeholder="Describe el motivo del reembolso…"
            className="w-full px-3 py-2.5 text-sm rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-amber-400 resize-none"
          />
        </div>
        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded-xl border border-slate-200 text-slate-600 bg-white hover:bg-slate-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => onConfirm(parsedAmount, reason)}
            disabled={!isValid || isLoading}
            className="px-5 py-2 text-sm font-semibold rounded-xl bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
          >
            {isLoading ? 'Procesando…' : 'Confirmar reembolso'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Main component ─── */
const PaymentDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();

  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [refundModalOpen, setRefundModalOpen] = useState(false);

  const { data: paymentResponse, isLoading } = useQuery<{ data: { data: Payment } }>(
    ['payment-detail', id],
    () => paymentsAPI.getById(id),
    { enabled: Boolean(id) }
  );

  const { data: auditResponse } = useQuery<{ data: { data: any[] } }>(
    ['payment-audit', id],
    () => auditLogsAPI.list({ entityType: 'PAYMENT', search: String(id), limit: 20 }),
    { enabled: Boolean(id) }
  );

  const invalidate = () => {
    queryClient.invalidateQueries(['payment-detail', id]);
    queryClient.invalidateQueries('payments');
    queryClient.invalidateQueries('payment-stats');
    queryClient.invalidateQueries('invoices');
  };

  const confirmMutation = useMutation<any, any, void>(
    () => paymentsAPI.confirm(Number(id), {}),
    {
      onSuccess: () => { toast.success('Pago confirmado exitosamente'); invalidate(); },
      onError: (e: any) => { toast.error(e.response?.data?.error || 'Error al confirmar el pago'); },
    }
  );

  const cancelMutation = useMutation<any, any, string>(
    (reason: string) => paymentsAPI.cancel(Number(id), { reason }),
    {
      onSuccess: () => { toast.success('Pago anulado exitosamente'); setCancelModalOpen(false); invalidate(); },
      onError: (e: any) => { toast.error(e.response?.data?.error || 'Error al anular el pago'); },
    }
  );

  const refundMutation = useMutation<any, any, { amount: number; reason: string }>(
    ({ amount, reason }) => paymentsAPI.refund(Number(id), { amount, reason }),
    {
      onSuccess: () => { toast.success('Reembolso procesado exitosamente'); setRefundModalOpen(false); invalidate(); },
      onError: (e: any) => { toast.error(e.response?.data?.error || 'Error al procesar el reembolso'); },
    }
  );

  const payment = paymentResponse?.data?.data;
  const allocations = payment?.allocations || [];
  const refunds = payment?.refunds || [];
  const audits = auditResponse?.data?.data || [];

  const handleDownloadReceipt = async () => {
    if (!id) return;
    try {
      const response = await paymentsAPI.downloadReceipt(Number(id));
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `recibo_pago_${id}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('No se pudo generar el recibo PDF');
    }
  };

  const allocationColumns: TableColumn<any>[] = [
    {
      key: 'invoice',
      label: 'Factura',
      render: (_, row) => (
        <div>
          <p className="font-medium text-slate-900">{row.invoice?.invoiceNumber || `#${row.invoice?.id}`}</p>
          <p className="text-xs text-slate-500">
            Vence: {row.invoice?.dueDate ? new Date(row.invoice.dueDate).toLocaleDateString() : 'N/A'}
          </p>
        </div>
      ),
    },
    {
      key: 'amountAppliedMinor',
      label: 'Monto aplicado',
      render: (value) => (
        <span className="font-semibold text-slate-900">{formatCurrency(value)}</span>
      ),
    },
    {
      key: 'invoice',
      label: 'Saldo restante',
      render: (_, row) => (
        <span className={`text-sm font-medium ${row.invoice?.balanceDueMinor === 0 ? 'text-emerald-600' : 'text-amber-700'}`}>
          {row.invoice != null ? formatCurrency(row.invoice.balanceDueMinor) : '—'}
        </span>
      ),
    },
    {
      key: 'createdAt',
      label: 'Fecha aplicación',
      render: (value) => (
        <span className="text-sm text-slate-600">{new Date(value).toLocaleString()}</span>
      ),
    },
  ];

  const refundColumns: TableColumn<any>[] = [
    {
      key: 'amountMinor',
      label: 'Monto',
      render: (value) => <span className="font-semibold text-rose-700">{formatCurrency(value)}</span>,
    },
    { key: 'reason', label: 'Motivo' },
    {
      key: 'status',
      label: 'Estado',
      render: (value) => {
        const key = value === 'PROCESSED' ? 'REFUNDED' : value;
        return <StatusBadge tone={STATUS_TONE[key] || 'info'}>{STATUS_LABEL[key] || value}</StatusBadge>;
      },
    },
    {
      key: 'processedAt',
      label: 'Procesado',
      render: (value) => <span className="text-sm text-slate-600">{value ? new Date(value).toLocaleString() : '—'}</span>,
    },
  ];

  if (isLoading || !payment) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const status = payment.status || (payment.isVerified ? 'CONFIRMED' : 'PENDING');
  const isTransfer = TRANSFER_METHODS.includes(payment.paymentMethod);
  const impactedInvoices = allocations.length;

  // Calculate refunded amount and available for refund
  const refundedMinor = refunds
    .filter((r: any) => r.status === 'PROCESSED')
    .reduce((sum: number, r: any) => sum + (r.amountMinor || 0), 0);
  const availableForRefund = ((payment.amountMinor || 0) - refundedMinor) / 100;

  const canConfirm = status === 'PENDING' && hasPermission('payments.verify');
  const canCancel = (status === 'PENDING' || status === 'CONFIRMED') && hasPermission('payments.delete');
  const canRefund = (status === 'CONFIRMED' || status === 'PARTIAL_REFUND') && availableForRefund > 0 && hasPermission('payments.delete');

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader
        title={`Pago #${payment.id}`}
        subtitle="Resumen completo, aplicaciones a facturas y auditoría."
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            {canConfirm && (
              <Button
                variant="outline"
                onClick={() => confirmMutation.mutate()}
                disabled={confirmMutation.isLoading}
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              >
                <CheckCircleIcon className="h-4 w-4 mr-2" />
                {confirmMutation.isLoading ? 'Confirmando…' : 'Confirmar'}
              </Button>
            )}
            {canRefund && (
              <Button
                variant="outline"
                onClick={() => setRefundModalOpen(true)}
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
              >
                <ArrowUturnLeftIcon className="h-4 w-4 mr-2" />
                Reembolsar
              </Button>
            )}
            {canCancel && (
              <Button
                variant="outline"
                onClick={() => setCancelModalOpen(true)}
                className="border-rose-300 text-rose-700 hover:bg-rose-50"
              >
                <XCircleIcon className="h-4 w-4 mr-2" />
                Anular
              </Button>
            )}
            <Button variant="outline" onClick={handleDownloadReceipt}>
              <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
              Recibo PDF
            </Button>
            <Button variant="outline" onClick={() => navigate('/payments')}>
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Volver
            </Button>
          </div>
        }
      />

      {/* Top KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 mb-2">
            <BanknotesIcon className="h-4 w-4" />
            Monto
          </div>
          <p className="text-2xl font-bold text-slate-900">
            {formatCurrency(payment.amountMinor, payment.amount)}
          </p>
          {refundedMinor > 0 && (
            <p className="text-xs text-rose-600 mt-1">
              -{formatCurrency(refundedMinor)} reembolsado
            </p>
          )}
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 mb-2">
            <CreditCardIcon className="h-4 w-4" />
            Estado
          </div>
          <div className="mt-1">
            <StatusBadge tone={STATUS_TONE[status] || 'info'}>{STATUS_LABEL[status] || status}</StatusBadge>
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 mb-2">
            <UserIcon className="h-4 w-4" />
            Cliente
          </div>
          <button
            type="button"
            onClick={() => payment.clientId && navigate(`/clients/${payment.clientId}`)}
            className="text-left hover:text-primary-700 transition-colors group"
          >
            <p className="text-base font-semibold text-slate-900 group-hover:text-primary-700">
              {payment.client?.name || 'N/A'}
            </p>
            <p className="text-xs text-slate-500">{payment.client?.cedula || '—'}</p>
          </button>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 mb-2">
            <DocumentDuplicateIcon className="h-4 w-4" />
            Facturas impactadas
          </div>
          <p className="text-2xl font-bold text-slate-900">{impactedInvoices}</p>
          {impactedInvoices === 0 && (
            <p className="text-xs text-slate-500 mt-1">Crédito no aplicado</p>
          )}
        </Card>
      </div>

      {/* Main info grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Payment summary */}
        <Card className="p-6">
          <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <CreditCardIcon className="h-4 w-4 text-slate-500" />
            Resumen del pago
          </h3>
          <dl className="space-y-3">
            <div className="flex items-center justify-between">
              <dt className="text-sm text-slate-500">Método</dt>
              <dd>
                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${METHOD_COLORS[payment.paymentMethod] || 'bg-slate-100 text-slate-700'}`}>
                  {METHOD_LABELS[payment.paymentMethod] || payment.paymentMethod}
                </span>
              </dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-sm text-slate-500">Referencia</dt>
              <dd className="text-sm font-medium text-slate-900 font-mono">{payment.reference || '—'}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-sm text-slate-500">Fecha de pago</dt>
              <dd className="text-sm font-medium text-slate-900 flex items-center gap-1">
                <CalendarIcon className="h-3.5 w-3.5 text-slate-400" />
                {formatDate(payment.paidAt || payment.paymentDate)}
              </dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-sm text-slate-500">Registrado por</dt>
              <dd className="text-sm font-medium text-slate-900">{payment.receivedBy || '—'}</dd>
            </div>
            {payment.isVerified && (
              <div className="flex items-center justify-between">
                <dt className="text-sm text-slate-500">Verificado</dt>
                <dd className="flex items-center gap-1 text-sm font-medium text-emerald-700">
                  <CheckCircleIcon className="h-4 w-4" />
                  Sí
                </dd>
              </div>
            )}
            {refundedMinor > 0 && (
              <div className="flex items-center justify-between border-t border-slate-100 pt-3">
                <dt className="text-sm text-slate-500">Reembolsado</dt>
                <dd className="text-sm font-semibold text-rose-700">{formatCurrency(refundedMinor)}</dd>
              </div>
            )}
            {payment.notes && (
              <div className="pt-3 border-t border-slate-100">
                <dt className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Notas</dt>
                <dd className="text-sm text-slate-700 bg-slate-50 rounded-lg px-3 py-2">{payment.notes}</dd>
              </div>
            )}
          </dl>
        </Card>

        {/* Bank details or audit */}
        {isTransfer ? (
          <Card className="p-6">
            <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <BuildingOfficeIcon className="h-4 w-4 text-slate-500" />
              Datos bancarios
            </h3>
            <dl className="space-y-3">
              {(payment as any).bankName && (
                <div className="flex items-center justify-between">
                  <dt className="text-sm text-slate-500">Banco</dt>
                  <dd className="text-sm font-medium text-slate-900">{(payment as any).bankName}</dd>
                </div>
              )}
              {(payment as any).bankReference && (
                <div className="flex items-center justify-between">
                  <dt className="text-sm text-slate-500">Ref. bancaria</dt>
                  <dd className="text-sm font-medium text-slate-900 font-mono">{(payment as any).bankReference}</dd>
                </div>
              )}
              {(payment as any).accountNumber && (
                <div className="flex items-center justify-between">
                  <dt className="text-sm text-slate-500">N° cuenta</dt>
                  <dd className="text-sm font-medium text-slate-900 font-mono">{(payment as any).accountNumber}</dd>
                </div>
              )}
              {(payment as any).transferDate && (
                <div className="flex items-center justify-between">
                  <dt className="text-sm text-slate-500">Fecha transferencia</dt>
                  <dd className="text-sm font-medium text-slate-900">{formatDate((payment as any).transferDate)}</dd>
                </div>
              )}
              {(payment as any).receiptFile && (
                <div className="pt-3 border-t border-slate-100">
                  <dt className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Comprobante</dt>
                  <dd className="text-sm text-slate-700 truncate">{(payment as any).receiptFile}</dd>
                </div>
              )}
            </dl>
            {!(payment as any).bankName && !(payment as any).bankReference && (
              <p className="text-sm text-slate-500">No se registraron datos bancarios adicionales.</p>
            )}
          </Card>
        ) : (
          <Card className="p-6">
            <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <ClockIcon className="h-4 w-4 text-slate-500" />
              Historial / auditoría
            </h3>
            <AuditTimeline audits={audits} />
          </Card>
        )}
      </div>

      {/* Audit for transfers (below bank details) */}
      {isTransfer && (
        <Card className="p-6">
          <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <ClockIcon className="h-4 w-4 text-slate-500" />
            Historial / auditoría
          </h3>
          <AuditTimeline audits={audits} />
        </Card>
      )}

      {/* Invoice allocations */}
      {allocations.length > 0 && (
        <DataTableCard>
          <div className="px-5 py-4 border-b border-slate-200">
            <h3 className="text-sm font-semibold text-slate-900">Aplicaciones a facturas</h3>
          </div>
          <Table data={allocations} columns={allocationColumns} emptyMessage="Este pago no tiene aplicaciones." />
        </DataTableCard>
      )}

      {/* Refunds */}
      {refunds.length > 0 && (
        <DataTableCard>
          <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
            <DocumentTextIcon className="h-5 w-5 text-slate-500" />
            <h3 className="text-sm font-semibold text-slate-900">Reembolsos</h3>
          </div>
          <Table data={refunds} columns={refundColumns} emptyMessage="Este pago no tiene reembolsos." />
        </DataTableCard>
      )}

      {/* Modals */}
      <CancelModal
        isOpen={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        onConfirm={(reason) => cancelMutation.mutate(reason)}
        isLoading={cancelMutation.isLoading}
      />
      <RefundModal
        isOpen={refundModalOpen}
        maxAmount={availableForRefund}
        onClose={() => setRefundModalOpen(false)}
        onConfirm={(amount, reason) => refundMutation.mutate({ amount, reason })}
        isLoading={refundMutation.isLoading}
      />
    </div>
  );
};

const AuditTimeline: React.FC<{ audits: any[] }> = ({ audits }) => {
  if (audits.length === 0) {
    return <p className="text-sm text-slate-500">Sin eventos de auditoría visibles para este pago.</p>;
  }
  return (
    <div className="space-y-3 max-h-64 overflow-auto">
      {audits.map((audit: any) => (
        <div key={audit.id} className="rounded-xl border border-slate-200 p-3 bg-white">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-semibold text-slate-700">{audit.action}</span>
            <span className="text-xs text-slate-400">
              {new Date(audit.createdAt).toLocaleString()}
            </span>
          </div>
          <p className="mt-1 text-sm text-slate-800">{audit.description}</p>
          <p className="mt-1 text-xs text-slate-500">Por: {audit.user?.name || 'Sistema'}</p>
        </div>
      ))}
    </div>
  );
};

export default PaymentDetail;
